import { Badge, Group, ScrollArea, Text, ThemeIcon, Tooltip, UnstyledButton } from '@mantine/core'
import type { ReactNode } from 'react'
import { IconUsers } from '@/components/icons'
import { ProjectIcon } from '@/components/sidebar/ProjectIcon'
import {
  KNOWLEDGE_GENERAL_TAB_ID,
  articleCountLabel,
  isKnowledgeGeneralTab,
} from '@/components/knowledge/knowledge-base-shared'
import type { ProjectRow, SupportKnowledgeArticleRow } from '@/types/api'

type Bundle = {
  project: ProjectRow
  articles: SupportKnowledgeArticleRow[]
}

type Props = {
  bundles: Bundle[]
  generalCount: number
  value: string | null
  onChange: (projectId: string) => void
}

function projectLabel(project: ProjectRow): string {
  return (project.name || project.slug || project.id).trim()
}

function TabShell({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <UnstyledButton
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      px="sm"
      py={8}
      style={{
        flexShrink: 0,
        borderRadius: 'var(--mantine-radius-md)',
        border: active
          ? '1px solid var(--mantine-color-brand-outline)'
          : '1px solid var(--mantine-color-default-border)',
        background: active ? 'var(--mantine-color-brand-light)' : 'var(--mantine-color-body)',
      }}
    >
      {children}
    </UnstyledButton>
  )
}

function ProjectTabButton({
  project,
  articles,
  active,
  onClick,
}: {
  project: ProjectRow
  articles: SupportKnowledgeArticleRow[]
  active: boolean
  onClick: () => void
}) {
  const label = projectLabel(project)

  return (
    <TabShell active={active} onClick={onClick}>
      <Group gap={8} wrap="nowrap">
        <ProjectIcon project={project} boxSize={20} />
        <Text size="sm" fw={active ? 600 : 500} style={{ whiteSpace: 'nowrap' }}>
          {label}
        </Text>
        <Badge size="xs" variant="light" color={active ? 'brand' : 'gray'}>
          {articles.length}
        </Badge>
      </Group>
    </TabShell>
  )
}

function GeneralTabButton({ count, active, onClick }: { count: number; active: boolean; onClick: () => void }) {
  return (
    <TabShell active={active} onClick={onClick}>
      <Group gap={8} wrap="nowrap">
        <ThemeIcon size={20} radius="sm" variant="light" color={active ? 'brand' : 'gray'}>
          <IconUsers size={14} stroke={1.5} />
        </ThemeIcon>
        <Text size="sm" fw={active ? 600 : 500} style={{ whiteSpace: 'nowrap' }}>
          Общие
        </Text>
        <Badge size="xs" variant="light" color={active ? 'brand' : 'gray'}>
          {count}
        </Badge>
      </Group>
    </TabShell>
  )
}

export function KnowledgeProjectTabs({ bundles, generalCount, value, onChange }: Props) {
  return (
    <ScrollArea type="auto" offsetScrollbars scrollbarSize={8} scrollbars="x">
      <Group gap={8} wrap="nowrap" pb={4} pr={4}>
        <GeneralTabButton
          count={generalCount}
          active={isKnowledgeGeneralTab(value)}
          onClick={() => onChange(KNOWLEDGE_GENERAL_TAB_ID)}
        />

        {bundles.map(({ project, articles }) => {
          const active = project.id === value
          const label = projectLabel(project)
          const button = (
            <ProjectTabButton
              key={project.id}
              project={project}
              articles={articles}
              active={active}
              onClick={() => onChange(project.id)}
            />
          )

          if (label.length > 28) {
            return (
              <Tooltip
                key={project.id}
                label={`${label} · ${articleCountLabel(articles.length)}`}
                withArrow
              >
                {button}
              </Tooltip>
            )
          }

          return button
        })}
      </Group>
    </ScrollArea>
  )
}
